import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { authService } from './services/auth';

// Layout Components
import Sidebar from './components/Layout/Sidebar';
import TopHeader from './components/Layout/TopHeader';

// Auth Pages
import Login from './pages/Auth/Login';

// Main Pages
import Dashboard from './pages/Dashboard/Dashboard';
import ProductList from './pages/Products/ProductList';
import ProductForm from './pages/Products/ProductForm';
import LivestreamList from './pages/Livestreams/LivestreamList';
import LivestreamForm from './pages/Livestreams/LivestreamForm';
import LivestreamEdit from './pages/Livestreams/LivestreamEdit';
import EnhancedLivestreamControlPanel from './pages/Livestreams/EnhancedLivestreamControlPanel';
import LivestreamAnalytics from './pages/Livestreams/LivestreamAnalytics';
import LivestreamDetails from './pages/Livestreams/LivestreamDetails';
import OrdersManagement from './pages/Orders/OrdersManagement';
import PaymentsManagement from './pages/Payments/PaymentsManagement';
import CreditsManagement from './pages/Credits/CreditsManagementNew';
import ProfileManagement from './pages/Profile/ProfileManagement';

// Common Components
import LoadingSpinner from './components/Common/LoadingSpinner';

// CSS
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const user = JSON.parse(localStorage.getItem('user'));

  if (!authService.isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  // Check if seller account is required
  if (user && user.user_type !== 'seller') {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  if (authService.isAuthenticated()) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(window.innerWidth < 1024);

  useEffect(() => {
    checkAuth();

    const handleResize = () => {
      // On desktop, we might want to automatically expand if it was collapsed by mobile logic
      // but usually it's better to just let the user toggle.
      // However, making sure it hides/shows transitionally is good.
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const checkAuth = async () => {
    try {
      if (authService.isAuthenticated()) {
        // Always fetch fresh user data from the backend
        const profileData = await authService.getProfile();
        setUser(profileData);
        localStorage.setItem('user', JSON.stringify(profileData));
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Clear invalid auth data
      authService.logout();
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    try {
      if (authService.isAuthenticated()) {
        const profileData = await authService.getProfile();
        setUser(profileData);
        localStorage.setItem('user', JSON.stringify(profileData));
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: 'var(--color-dark-900)' }}>
        <LoadingSpinner text="Loading application..." />
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        {user && (
          <Sidebar
            user={user}
            onLogout={handleLogout}
            isCollapsed={isSidebarCollapsed}
            onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          />
        )}

        <div className={user ? `main-content ${isSidebarCollapsed ? 'collapsed' : ''}` : ''}>
          {user && (
            <TopHeader
              user={user}
              onLogout={handleLogout}
              isSidebarCollapsed={isSidebarCollapsed}
              toggleSidebar={toggleSidebar}
            />
          )}

          <main>
            <Routes>
              {/* Public Routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login onLogin={handleLogin} />
                  </PublicRoute>
                }
              />


              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard user={user} refreshUser={refreshUser} />
                  </ProtectedRoute>
                }
              />

              {/* Product Routes */}
              <Route
                path="/products"
                element={
                  <ProtectedRoute>
                    <ProductList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/create"
                element={
                  <ProtectedRoute>
                    <ProductForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/:id/edit"
                element={
                  <ProtectedRoute>
                    <ProductForm />
                  </ProtectedRoute>
                }
              />

              {/* Livestream Routes */}
              <Route
                path="/livestreams"
                element={
                  <ProtectedRoute>
                    <LivestreamList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/livestreams/create"
                element={
                  <ProtectedRoute>
                    <LivestreamForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/livestreams/:id/edit"
                element={
                  <ProtectedRoute>
                    <LivestreamEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/livestreams/:id/analytics"
                element={
                  <ProtectedRoute>
                    <LivestreamAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/livestreams/:id/details"
                element={
                  <ProtectedRoute>
                    <LivestreamDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/livestreams/:livestreamId/control"
                element={
                  <ProtectedRoute>
                    <EnhancedLivestreamControlPanel />
                  </ProtectedRoute>
                }
              />

              {/* Order Management */}
              <Route
                path="/orders"
                element={
                  <ProtectedRoute>
                    <OrdersManagement />
                  </ProtectedRoute>
                }
              />

              {/* Payments & Withdrawals */}
              <Route
                path="/payments"
                element={
                  <ProtectedRoute>
                    <PaymentsManagement />
                  </ProtectedRoute>
                }
              />

              {/* Credits Management */}
              <Route
                path="/credits"
                element={
                  <ProtectedRoute>
                    <CreditsManagement refreshUser={refreshUser} />
                  </ProtectedRoute>
                }
              />

              {/* Profile Management */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfileManagement refreshUser={refreshUser} />
                  </ProtectedRoute>
                }
              />

              {/* Redirect root to appropriate page */}
              <Route
                path="/"
                element={
                  user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
                }
              />

              {/* 404 Route */}
              <Route
                path="*"
                element={
                  <div className="container-fluid py-4">
                    <div className="text-center text-white">
                      <h1>404 - Page Not Found</h1>
                      <p>The page you're looking for doesn't exist.</p>
                      <button
                        className="btn btn-primary"
                        onClick={() => window.history.back()}
                      >
                        Go Back
                      </button>
                    </div>
                  </div>
                }
              />
            </Routes>
          </main>


        </div>
      </div>
    </Router>
  );
}

export default App;




